import { Col, Row, Form, Button, Spinner } from "react-bootstrap";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { ClaimFileDto, ClaimOutletContext, TopicAIDto } from "../../types";
import { useCurrentClient } from "../../../auth/api/getCurrentClient";
import SentimentIndicator from "../../../../components/SentimentIndicatorSelector";
import GenerateAlertSelector from "../../../../components/GenerateAlertSelctor";
import { ClaimStatusEnum } from "../../types/ClaimStatusEnum";
import TopicsSection from "../../../topics/components/TopicsSection";
import { getRecommendationByAI, getSentimentsTopicsAndAlertsByAI, getSuggestedMessageByAI } from "../../api/ClaimAPI";
import { ApiErrorResponse } from "../../../../types/ApiErrorResponse";
import ModalGlobal from "../../../../components/AppModal/ModalGlobal";
import AlertMessage from "../../../../components/AlertMessage";
import { useAlertAIHandler } from "../../../../hooks/useAlertForKanbanState";
import { useAlert } from "../../../../hooks/useAlert";
import { Topic } from "../../../topics/types";
import SendEmailToPatientPage from "./SendEmailToPatientPage";
import { PERMISSIONS } from "../../../../constants/permissions";
import { mergeFiles } from "../../../../utils/ClaimFileUtils";
import { useUserPermissions } from "../../../../utils/auth";

export default function EditClaimRecomendation() {
    const { t } = useTranslation('claim'); 
    const { claimUpdate, setClaimUpdate } = useOutletContext<ClaimOutletContext>();

    const [searchParams] = useSearchParams();
    const mode = searchParams.get('mode');
    
    const disabled = claimUpdate.status == ClaimStatusEnum.Closed || mode == 'view';
    const [isLoadingRecommendation, setIsLoadingRecommendation] = useState(false);
    const [recommendationError, setRecommendationError] = useState<string | null>(null);
    const [isLoadingSuggestedMessage, setIsLoadingSuggestedMessage] = useState(false);
    const [isLoadingGetSentimentsTopicsAndAlerts, setIsLoadingGetSentimentsTopicsAndAlerts] = useState(false);
    const [suggestedMessageError, setSuggestedMessageError] = useState<string | null>(null);
    const { data: currentClient } = useCurrentClient();
    const userPermissions = useUserPermissions();
    const { alert: alertMain, showAlert, isVisible: isAlertVisible } = useAlert({ fromLocation: false });
    const {
        handleAlertAI,
        handleTopicAlertAI,
        handleDeleteAlert,
        showModalDeleteAlert,
        alert, hideAlert, isVisible,
    } = useAlertAIHandler({
        state: claimUpdate,
        setAlertAI: (v) => setClaimUpdate({ ...claimUpdate, alertAI: v }),
        setTopicAlertAI: (i, v) => {
            const newTopics = [...claimUpdate.topics];
            newTopics[i] = { ...newTopics[i], alertAI: parseInt(v) };
            setClaimUpdate({ ...claimUpdate, topics: newTopics });
        },
        onDeleteAlertConfirmed: () => setClaimUpdate(prev => prev && { ...prev, deleteAlert: true }),
        fromLocation: false,
    });
    const [showSendEmailPage, setShowSendEmailPage] = useState<boolean>(false);

    const askRecommendation = async () => {
        if (!claimUpdate || isLoadingRecommendation) return;

        setRecommendationError(null);
        setIsLoadingRecommendation(true);
        try {
            const data = await getRecommendationByAI(claimUpdate);
            setClaimUpdate({
                ...claimUpdate,
                recommendationAI: data,
            });
        } catch (error) {
            const fetchError = error as ApiErrorResponse;
            setRecommendationError(fetchError?.message || t("detail.askRecommendationError"));
        } finally {
            setIsLoadingRecommendation(false);
        }
    };
    
    const askSuggestedMessage = async () => {
        if (!claimUpdate?.id || isLoadingSuggestedMessage) return;

        setSuggestedMessageError(null);
        setIsLoadingSuggestedMessage(true);
        try {
            const data = await getSuggestedMessageByAI(claimUpdate.id);
            setClaimUpdate({
                ...claimUpdate,
                messageAI: data,
            });
        } catch (error) {
            const fetchError = error as ApiErrorResponse;
            setSuggestedMessageError(fetchError?.message || t("detail.askSuggestedMessageError"));
        } finally {
            setIsLoadingSuggestedMessage(false);
        }
    };

    const mapTopicAIDtoToTopic = (topicAI: TopicAIDto): Topic => ({
        topicId: topicAI.genericTopicId,
        topicName: topicAI.genericTopicName,
        subtopicId: topicAI.topicId,
        subtopicName: topicAI.topicName,
        alertAI: topicAI.alertScore,
        sentimentAI: topicAI.feelingNote,
        originalAlertAI: topicAI.alertScore,
        originalSentimentAI: topicAI.feelingNote,
    });

    const getSentimentsTopicsAndAlerts = async () => {
        if (!claimUpdate?.id || isLoadingGetSentimentsTopicsAndAlerts) return;
        setIsLoadingGetSentimentsTopicsAndAlerts(true);
        try {
            showAlert({
                message: t("detail.getAISentimentAlertAndTopicsMessage"),
                type: "success",
                duration: 5000,
            });
            const data = await getSentimentsTopicsAndAlertsByAI(claimUpdate.id);
            const topics = data.topics.map(mapTopicAIDtoToTopic);
            setClaimUpdate({
                ...claimUpdate,
                alertAI: data.alertScore,
                sentimentAI: data.feelingNote,
                topics: topics 
            });
        } catch (error) {
            const fetchError = error as ApiErrorResponse;
            const errorMessasge = fetchError?.message || t("detail.getAISentimentAlertAndTopicsError");
            showAlert({
                message: errorMessasge,
                type: "danger",
                duration: 5000,
            });
        } finally {
            setIsLoadingGetSentimentsTopicsAndAlerts(false);
        }
    };
    
    const emailSendedSuccessfully = (claimFile: ClaimFileDto) => {        
        setClaimUpdate((prev) => {
            if (!prev) return prev;
            return { ...prev, files: mergeFiles([claimFile], prev.files ?? []) };
        });
        
        showAlert({
            message: t("claim:sendEmailPatient.successMessage"),
            type: "success",
            duration: 3000,
        })
    }

    return (<>
        {showSendEmailPage ?
            <SendEmailToPatientPage
                claimId={claimUpdate.id}
                affectedPersonEmailAddress={claimUpdate.personAffected?.email ?? ""}
                claimantEmailAddress={claimUpdate.email ?? ""}
                suggestedMessage={claimUpdate.messageAI}
                setShowSendEmailPage={setShowSendEmailPage}
                onEmailSendedSuccessfully={emailSendedSuccessfully}
            /> :
            <>
                <ModalGlobal
                    show={showModalDeleteAlert}
                    onClose={() => handleDeleteAlert(false)}
                    body={t("claim:modal.modalDeleteAlertTitle", { alertId: claimUpdate.alertId })}
                    onSave={() => {
                        handleDeleteAlert(true);
                    }}
                    saveText={t("common:buttons.buttonYes")}
                    closeText={t("common:buttons.buttonNo")}
                    isLoading={false}
                />
                <Row className="w-100">
                    <Col>
                        <Row className="mt-4 mb-4">
                            <Col xs={8} md={8}>
                                <h5>{t("claim:detail.dataClaimAIHeaderSentimentAnalysis")}</h5>
                            </Col>
                            <Col xs={4} md={4} className="d-flex gap-2 justify-content-end">
                                {(userPermissions.length > 0 && userPermissions.includes(PERMISSIONS.CLAIMS.SEND_EMAIL)) && (
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        onClick={() => setShowSendEmailPage(true)}
                                    >
                                        {t("claim:button.sendEmailToPatient")}
                                    </Button>
                                )}
                                <Button
                                    type="button"
                                    variant="secondary"
                                    disabled={disabled || isLoadingGetSentimentsTopicsAndAlerts}
                                    onClick={getSentimentsTopicsAndAlerts}
                                >
                                    {isLoadingGetSentimentsTopicsAndAlerts && (
                                        <Spinner animation="border" size="sm" className="me-2" />
                                    )}
                                    {t("claim:button.getAISentimentAlertAndTopics")}
                                </Button>
                            </Col>
                        </Row>
                        <Row>
                            {(isVisible || isAlertVisible) && (
                                <AlertMessage
                                    message={alert?.message ?? alertMain?.message ?? ""}
                                    type={alert?.type ?? alertMain?.type ?? "danger"}
                                    onClose={hideAlert}
                                />
                            )}
                        </Row>
                        <Row>
                            <Col xs={6} md={6}>                    
                                <SentimentIndicator
                                    value={claimUpdate.sentimentAI ?? null}
                                    onChange={(newValue) => {
                                        setClaimUpdate({
                                            ...claimUpdate,
                                            sentimentAI: newValue
                                        });
                                    }}
                                    disabled={disabled}
                                    label={t("claim:newClaimFeeling")}
                                    minValue={currentClient?.sentimentMinimumValue}
                                    maxValue={currentClient?.sentimentMaximumValue}
                                />
                            </Col>
                            <Col xs={6} md={6}>
                                <GenerateAlertSelector
                                    value={claimUpdate.alertAI ?? null}
                                    disabled={disabled}
                                    label={t("claim:newClaimGenerateAlert")}
                                    minValue={currentClient?.sentimentMinimumValue}
                                    maxValue={currentClient?.sentimentMaximumValue}
                                    neutralReference={currentClient?.neutralReference}
                                    alertMinimumValue={currentClient?.alertMinimumValue}
                                    alertMaximumValue={currentClient?.alertMaximumValue}
                                    onChange={(newValue) => handleAlertAI(newValue) }
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col xs={8} md={8} className="mt-4">
                                <h5>{t("claim:detail.dataClaimAIHeaderTopics")}</h5>
                            </Col>
                        </Row>
                        <Row>
                            <Col xs={12} md={12}>
                                <TopicsSection
                                    topics={claimUpdate.topics}
                                    setTopics={(selectedTopics) => {
                                        setClaimUpdate((prev) => {
                                            if (!prev) return prev;

                                            const nextTopics = typeof selectedTopics === "function"
                                                ? selectedTopics(prev.topics)
                                                : selectedTopics;

                                            return {
                                                ...prev,
                                                topics: nextTopics,
                                            };
                                        });
                                    }}
                                    editing={!disabled}
                                    disabled={disabled}
                                    newTopicLabel={t("claim:button.newTopic")}
                                    onChangeAlertAI={(newValue, index) => handleTopicAlertAI(newValue, index)}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col xs={8} md={8} className="mt-4">
                                <h5>{t("claim:detail.headerSuggerimentsIA")}</h5>
                            </Col>
                        </Row>
                        <Row >
                            <Col xs={12} md={12}>
                                <Form.Group>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <Form.Label>{t("claim:detail.showDetailClaimMessageAI")}</Form.Label>
                                        <div className="d-flex" style={{ gap: "10px" }}>
                                            <Button
                                                type="button"
                                                variant="secondary"
                                                disabled={disabled || isLoadingSuggestedMessage}
                                                onClick={askSuggestedMessage}
                                            >
                                                {isLoadingSuggestedMessage && (
                                                    <Spinner animation="border" size="sm" className="me-2" />
                                                )}
                                                {t("claim:button.askSuggestedMessage")}
                                            </Button>
                                            <Button type="button" variant="secondary" onClick={() => navigator.clipboard.writeText(claimUpdate?.messageAI ?? "")}>
                                                {t("claim:button.copy")}
                                            </Button>
                                        </div>
                                    </div>
                                    <Form.Control
                                        as="textarea"
                                        disabled={disabled}
                                        rows={5}
                                        value={claimUpdate?.messageAI ?? ""}
                                        onChange={(e) => {
                                            if (!claimUpdate) return;
                                            setClaimUpdate({
                                                ...claimUpdate,
                                                messageAI: e.target.value,
                                            })
                                        }
                                        }
                                    />
                                    {suggestedMessageError && (
                                        <div className="invalid-feedback d-block">
                                            {suggestedMessageError}
                                        </div>
                                    )}
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col xs={12} md={12}>
                                <Form.Group>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <Form.Label>{t("claim:detail.showDetailClaimRecommendationAI")}</Form.Label>
                                        <div className="d-flex" style={{ gap: "10px" }}>
                                            <Button
                                                type="button"
                                                variant="secondary"
                                                disabled={disabled || isLoadingRecommendation}
                                                onClick={askRecommendation}
                                            >
                                                {isLoadingRecommendation && (
                                                    <Spinner animation="border" size="sm" className="me-2" />
                                                )}
                                                {t("claim:button.askRecommendation")}
                                            </Button>
                                            <Button type="button" variant="secondary" onClick={() => navigator.clipboard.writeText(claimUpdate?.recommendationAI ?? "")}>
                                                {t("claim:button.copy")}
                                            </Button>
                                        </div>
                                    </div>
                                    <Form.Control
                                        as="textarea"
                                        disabled={disabled}
                                        rows={5}
                                        value={claimUpdate?.recommendationAI ?? ""}
                                        onChange={(e) => {
                                            if (!claimUpdate) return;
                                            setClaimUpdate({
                                                ...claimUpdate,
                                                recommendationAI: e.target.value,
                                            })
                                        }}
                                    />
                                    {recommendationError && (
                                        <div className="invalid-feedback d-block">
                                            {recommendationError}
                                        </div>
                                    )}
                                </Form.Group>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </>
        }
    </>);
}


